import {
  Box,
  CheckFilledIcon,
  CheckIcon,
  CloseIcon,
  GKButton,
  Grid2,
  Show,
  SimpleLoader,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { map } from "lodash";
import { Controller, FormProvider, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import AllocationTemplate from "./AllocationTemplate";

const tabValues = [
  {
    label: "Allocation",
    value: "allocation",
    subText: "Allocate new/fresh funds in a model or a company",
  },
  {
    label: "Rebalance",
    value: "rebalance",
    subText:
      "Rebalance client’s portfolio or single company in the client’s portfolio",
  },
  {
    label: "Exit",
    value: "exit",
    subText:
      "Exit or sell from a client’s portfolio or a single company in the client’s portfolio",
  },
];

const BasketTemplate = () => {
  const { data, isFetching } = useFetchQuery({
    key: ["GET_BASKET_TEMPLATE"],
    route: `/order_manager_template/`,
  });

  const { mutate, isLoading } = useMutation({
    mutationKey: ["BASKET_TEMPLATE"],
    mutationFn: (data) =>
      axiosInstance.patch(`/order_manager_template/save_template/`, {
        formData: data,
      }),
  });

  const form = useForm({
    defaultValues: {
      howItsWork: true,
      tabValue: "allocation",
      allocation: {
        allocationBy: "version",
        useCurrentAllocation: "",
        rebalanceUsing: "committedValue",
        weightageDistributionMethod: "PRORATA",
        // stockWise: false,
      },
      rebalance: {
        allocationBy: "version",
        useCurrentAllocation: "",
        rebalanceUsing: "current",
        weightageDistributionMethod: "PRORATA",
        exitParameter: "",
        buyParameter: "",
        // stockWise: false,
      },
      exit: {
        allocationBy: "version",
        useCurrentAllocation: "",
        rebalanceUsing: "",
        weightageDistributionMethod: "",
        // stockWise: false,
      },
    },
    values: data?.data?.data?.formData,
  });

  const watchAllocationType = form.watch("tabValue");
  const watchHowItsWork = form.watch("howItsWork");

  if (isFetching) return <SimpleLoader />;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit((values) => {
          mutate(values);
        })}
      >
        <Grid2 container spacing={4}>
          <Grid2 size={12}>
            <Typography fontSize={14} fontWeight={700} color=" #20272A">
              Create Templates for Generate Trades
            </Typography>
            <Typography fontSize={12} fontWeight={500} color=" #697586" mt={1}>
              Save time by creating templates for generating trades efficiently.
            </Typography>
          </Grid2>
          <Grid2 container size={12} spacing={3}>
            {map(tabValues, (tab) => {
              return (
                <Grid2 size={4}>
                  <Controller
                    name="tabValue"
                    control={form.control}
                    render={({ field }) => (
                      <Box
                        sx={{
                          border: `1px solid ${field.value === tab.value ? " #006580" : " #D5DEE5"}`,
                          background:
                            field.value === tab.value
                              ? " #E6F0F3"
                              : "var(--white)",
                          borderRadius: 2,
                          cursor: "pointer",
                          height: "100%",
                          "&:hover": {
                            borderColor: " #D5DEE5",
                            background: " #EEF5FA",
                          },
                        }}
                        onClick={() => field.onChange(tab.value)}
                      >
                        <Grid2 container spacing={1} p={2}>
                          <Grid2
                            size={12}
                            display={"flex"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                          >
                            <Typography
                              fontSize={14}
                              fontWeight={600}
                              color={
                                field.value === tab.value ? "#006580" : " #333"
                              }
                            >
                              {tab.label}
                            </Typography>
                            {field.value === tab.value && (
                              <CheckFilledIcon
                                width={20}
                                height={20}
                                color="#006580"
                              />
                            )}
                          </Grid2>
                          <Grid2 size={11}>
                            <Typography
                              fontSize={12}
                              fontWeight={500}
                              color=" #697586"
                            >
                              {tab.subText}
                            </Typography>
                          </Grid2>
                        </Grid2>
                      </Box>
                    )}
                  />
                </Grid2>
              );
            })}
          </Grid2>

          <Show.When isTrue={watchHowItsWork}>
            <Controller
              name="howItsWork"
              control={form.control}
              render={({ field }) => (
                <Grid2
                  size={8}
                  border={"1px solid  #D5DEE5"}
                  borderRadius={1}
                  bgcolor={" #EEF5FA"}
                  p={"12px"}
                >
                  <Typography
                    fontSize={14}
                    fontWeight={600}
                    color="#333"
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    How it Works?
                    <CloseIcon
                      style={{ cursor: "pointer" }}
                      onClick={() => field.onChange(false)}
                    />
                  </Typography>
                  <ul
                    style={{ fontSize: 13, fontWeight: 500, color: " #4F4F4F" }}
                  >
                    <li>
                      Save your preferred trade settings here. They’ll apply
                      automatically when you generate trades with an allocation.
                    </li>
                    <li style={{ marginTop: "8px" }}>
                      You can adjust them anytime here or during trade
                      generation.
                    </li>
                  </ul>
                </Grid2>
              )}
            />
          </Show.When>

          <Grid2 size={12}>
            {watchAllocationType === "allocation" && (
              <AllocationTemplate watchAllocationType={watchAllocationType} />
            )}
            {watchAllocationType === "rebalance" && (
              <AllocationTemplate watchAllocationType={watchAllocationType} />
            )}
            {watchAllocationType === "exit" && (
              <AllocationTemplate watchAllocationType={watchAllocationType} />
            )}
          </Grid2>

          <Grid2 size={4} />

          <Grid2 size={8}>
            <GKButton
              variant="outlined"
              color="dark"
              startIcon={<CheckIcon />}
              type="submit"
              loading={isLoading}
            >
              Save Template
            </GKButton>
          </Grid2>
        </Grid2>
      </form>
    </FormProvider>
  );
};

export default BasketTemplate;
